import {
  IRegistrationGuest,
  MAX_REGISTRATION_GUESTS,
} from "../interfaces/models/IRegistration";
import {
  getSpecialEventGuestsForProgram,
  normalizeSpecialEventGuestNames,
  type SpecialEventGuestNamesMap,
} from "./specialEventGuestNames";
import {
  applyGuestModificationDates,
  hasVisibleGuestWithMissingName,
  sanitizeGuestsForPersist,
} from "./registrationGuests";

export type SpecialEventGuestsMap = Record<string, IRegistrationGuest[]>;

type SpecialEventGuestSource = {
  selectedSpecialEventIds?: string[] | null;
  specialEventGuests?: SpecialEventGuestsMap | null;
  specialEventGuestNames?: Record<string, string | string[]> | null;
  registrationDate?: number | null;
};

export function normalizeSpecialEventGuestsMap(
  reg: SpecialEventGuestSource | null | undefined,
): SpecialEventGuestsMap {
  if (!reg) {
    return {};
  }

  const rootDate =
    typeof reg.registrationDate === "number" &&
    Number.isFinite(reg.registrationDate)
      ? reg.registrationDate
      : Math.floor(Date.now() / 1000);

  const fromRich: SpecialEventGuestsMap = {};
  if (reg.specialEventGuests && typeof reg.specialEventGuests === "object") {
    for (const [programId, guests] of Object.entries(reg.specialEventGuests)) {
      const sanitized = sanitizeGuestsForPersist(guests, rootDate);
      if (sanitized.length > 0) {
        fromRich[programId] = sanitized;
      }
    }
  }

  if (Object.keys(fromRich).length > 0) {
    return fromRich;
  }

  const legacyNames = normalizeSpecialEventGuestNames(
    reg.specialEventGuestNames,
  );
  const fromLegacy: SpecialEventGuestsMap = {};
  for (const [programId, names] of Object.entries(legacyNames)) {
    const guests = names
      .slice(0, MAX_REGISTRATION_GUESTS)
      .map((name, index) => ({
        id: `${programId}-guest-${index + 1}`,
        name,
        registrationDate: rootDate,
        modificationDate: rootDate,
      }));
    if (guests.length > 0) {
      fromLegacy[programId] = guests;
    }
  }
  return fromLegacy;
}

export function deriveSpecialEventGuestNames(
  guestsMap: SpecialEventGuestsMap,
): SpecialEventGuestNamesMap | undefined {
  const entries = Object.entries(guestsMap)
    .map(([programId, guests]) => {
      const names = guests
        .map((g) => g.name?.trim())
        .filter((name): name is string => Boolean(name));
      return [programId, names] as const;
    })
    .filter(([, names]) => names.length > 0);

  if (entries.length === 0) {
    return undefined;
  }
  return Object.fromEntries(entries);
}

export function sanitizeSpecialEventGuestsForPersist(
  guestsMap: SpecialEventGuestsMap | null | undefined,
  selectedProgramIds: string[],
  fallbackRegistrationDate?: number,
): SpecialEventGuestsMap {
  const selected = new Set(selectedProgramIds.filter(Boolean));
  const next: SpecialEventGuestsMap = {};
  if (!guestsMap || typeof guestsMap !== "object") {
    return next;
  }

  for (const programId of selected) {
    const sanitized = sanitizeGuestsForPersist(
      guestsMap[programId] ?? [],
      fallbackRegistrationDate,
    );
    if (sanitized.length > 0) {
      next[programId] = sanitized;
    }
  }
  return next;
}

export function applySpecialEventGuestModificationDates(
  next: SpecialEventGuestsMap,
  previous: SpecialEventGuestsMap | null | undefined,
  now = Math.floor(Date.now() / 1000),
): SpecialEventGuestsMap {
  const stamped: SpecialEventGuestsMap = {};
  for (const [programId, guests] of Object.entries(next)) {
    stamped[programId] = applyGuestModificationDates(
      guests,
      previous?.[programId],
      now,
    );
  }
  return stamped;
}

export function countSpecialEventGuestsForProgram(
  reg:
    | {
        specialEventGuests?: Record<
          string,
          Array<{ name?: string } | null | undefined>
        > | null;
        specialEventGuestNames?: Record<string, string | string[]> | null;
      }
    | null
    | undefined,
  programId: string,
): number {
  if (!reg || !programId) {
    return 0;
  }
  const rich = reg.specialEventGuests?.[programId];
  if (Array.isArray(rich)) {
    return sanitizeGuestsForPersist(rich).length;
  }
  return getSpecialEventGuestsForProgram(
    reg.specialEventGuestNames ?? {},
    programId,
  ).length;
}

export function hasSpecialEventGuestsWithMissingNames (
  guestsMap: SpecialEventGuestsMap,
  selectedProgramIds: string[],
): boolean {
  for (const programId of selectedProgramIds) {
    if (hasVisibleGuestWithMissingName(guestsMap[programId])) {
      return true;
    }
  }
  return false;
}
